import React, { useState, useEffect, useCallback } from 'react';
import { reportService } from '../../services/reportService';
import DateRangeFilter from './DateRangeFilter';
import { Download, AlertTriangle } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import SortableHeader, { useSort } from './SortableHeader';
import ChartContainer from './ChartContainer';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

const BillableHoursReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupBy, setGroupBy] = useState('attorney');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { group_by: groupBy };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await reportService.getBillableHours(params);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching billable hours:', err);
      setError('Failed to load Billable Hours report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, groupBy]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { sortConfig, requestSort, sortItems } = useSort('billable_hours', 'desc');

  const handleDateChange = ({ start_date, end_date }) => {
    setStartDate(start_date);
    setEndDate(end_date);
  };

  const handleExportCSV = () => {
    const params = { group_by: groupBy };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    reportService.downloadCSV(reportService.getBillableHours, params, 'billable_hours_report.csv');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-64 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
        <p className="text-gray-500">{error}</p>
        <button onClick={fetchData} className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm">Retry</button>
      </div>
    );
  }

  const { summary, items } = data || { summary: {}, items: [] };
  const sortedItems = sortItems(items);

  const chartData = items.slice(0, 10).map(item => ({
    name: item.group_key.length > 20 ? item.group_key.substring(0, 20) + '...' : item.group_key,
    billable: parseFloat(item.billable_hours),
    nonBillable: parseFloat(item.non_billable_hours),
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Billable Hours</h2>
        <button onClick={handleExportCSV} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <DateRangeFilter startDate={startDate} endDate={endDate} onChange={handleDateChange} />
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Group by:</span>
          {['attorney', 'case', 'case_type'].map((g) => (
            <button
              key={g}
              onClick={() => setGroupBy(g)}
              className={`px-3 py-1 text-sm rounded font-medium transition-colors ${
                groupBy === g ? 'bg-yellow-100 text-yellow-800' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {g === 'case_type' ? 'Case Type' : g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Total Hours</p>
          <p className="text-lg font-bold text-blue-700">{parseFloat(summary.total_hours || 0).toFixed(1)}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Billable Hours</p>
          <p className="text-lg font-bold text-green-700">{parseFloat(summary.total_billable_hours || 0).toFixed(1)}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Billed Hours</p>
          <p className="text-lg font-bold text-yellow-700">{parseFloat(summary.total_billed_hours || 0).toFixed(1)}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Billable Amount</p>
          <p className="text-lg font-bold text-purple-700">{formatCurrency(summary.total_billable_amount || 0)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Utilization Rate</p>
          <p className="text-lg font-bold text-gray-900">{summary.overall_utilization_rate || 0}%</p>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Hours by {groupBy === 'case_type' ? 'Case Type' : groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}</h3>
          <ChartContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} width={50} />
                <Tooltip />
                <Legend />
                <Bar dataKey="billable" name="Billable" fill="#CA8A04" radius={[4, 4, 0, 0]} />
                <Bar dataKey="nonBillable" name="Non-Billable" fill="#D1D5DB" radius={[4, 4, 0, 0]} />
              </BarChart>
          </ChartContainer>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label={groupBy === 'case_type' ? 'Case Type' : groupBy.charAt(0).toUpperCase() + groupBy.slice(1)} sortKey="group_key" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Total" sortKey="total_hours" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Billable" sortKey="billable_hours" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Billed" sortKey="billed_hours" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Unbilled" sortKey="unbilled_hours" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Amount" sortKey="billable_amount" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Utilization" sortKey="utilization_rate" sortConfig={sortConfig} onSort={requestSort} align="right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedItems.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No time entries found</td></tr>
              ) : sortedItems.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.group_key}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{parseFloat(item.total_hours).toFixed(1)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{parseFloat(item.billable_hours).toFixed(1)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{parseFloat(item.billed_hours).toFixed(1)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{parseFloat(item.unbilled_hours).toFixed(1)}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(item.billable_amount)}</td>
                  <td className="px-4 py-3 text-sm text-right">
                    <span className={`${item.utilization_rate >= 70 ? 'text-green-700' : item.utilization_rate >= 50 ? 'text-yellow-700' : 'text-red-700'}`}>
                      {item.utilization_rate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BillableHoursReport;
